'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Hangul from 'hangul-js';
import VirtualKeyboard from './VirtualKeyboard';

export default function VirtualKeyboardProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [visible, setVisible] = useState(false);
  const [activeInput, setActiveInput] = useState<HTMLInputElement | null>(null);
  const [isShifted, setIsShifted] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<'ko' | 'en'>('ko');

  // Native setter 접근
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    typeof window !== 'undefined' ? window.HTMLInputElement.prototype : {},
    'value'
  )?.set;

  // 입력창 포커스 시 키보드 표시
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' &&
        (target as HTMLInputElement).type !== 'hidden' &&
        (target as HTMLInputElement).type !== 'checkbox' &&
        (target as HTMLInputElement).type !== 'radio'
      ) {
        setActiveInput(target as HTMLInputElement);
        setVisible(true);
        document.body.classList.add('keyboard-visible');

        // 입력창이 키보드에 가려지지 않도록 스크롤
        setTimeout(() => {
          const input = target as HTMLInputElement;
          const rect = input.getBoundingClientRect();
          const keyboardHeight = 320; // 키보드 높이 (px)
          const viewportHeight = window.innerHeight;
          const inputBottom = rect.bottom;

          if (inputBottom > viewportHeight - keyboardHeight) {
            const scrollAmount = inputBottom - (viewportHeight - keyboardHeight) + 20;
            window.scrollBy({ top: scrollAmount, behavior: 'smooth' });
          }
        }, 100);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const relatedTarget = e.relatedTarget as HTMLElement;

      // 키보드 버튼으로 포커스 이동한 경우는 무시
      if (relatedTarget && relatedTarget.closest('.virtual-keyboard')) {
        return;
      }

      // 다른 input으로 직접 이동하는 경우는 무시 (키보드 유지)
      if (
        relatedTarget &&
        relatedTarget.tagName === 'INPUT' &&
        (relatedTarget as HTMLInputElement).type !== 'hidden' &&
        (relatedTarget as HTMLInputElement).type !== 'checkbox' &&
        (relatedTarget as HTMLInputElement).type !== 'radio'
      ) {
        return;
      }

      // 그 외의 경우만 키보드 숨김
      if (target.tagName === 'INPUT') {
        setVisible(false);
        setActiveInput(null);
        document.body.classList.remove('keyboard-visible');
      }
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      document.body.classList.remove('keyboard-visible');
    };
  }, []);

  // 한글 키 입력 처리
  const handleKoreanKeyPress = useCallback(
    (key: string) => {
      if (!activeInput || !nativeInputValueSetter) return;

      const currentValue = activeInput.value;
      const cursorPos = activeInput.selectionStart || 0;
      const cursorEnd = activeInput.selectionEnd || 0;

      // 선택 영역이 있으면 삭제
      let valueBeforeCursor = currentValue.substring(0, cursorPos);
      const valueAfterCursor = currentValue.substring(cursorEnd);

      if (cursorPos !== cursorEnd) {
        valueBeforeCursor = currentValue.substring(0, cursorPos);
      }

      const lastChar = valueBeforeCursor.slice(-1);

      // 마지막 글자가 한글인지 확인
      const isKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(lastChar);

      if (isKorean && lastChar) {
        // 한글 분해
        const disassembled = Hangul.disassemble(lastChar);

        // 종성이 있고 + 새로운 키가 모음인 경우
        if (disassembled.length === 3 && /[ㅏ-ㅣ]/.test(key)) {
          // 종성을 초성으로 분리
          const [cho, jung, jong] = disassembled;
          const newFirstChar = Hangul.assemble([cho, jung]);
          const newSecondChar = Hangul.assemble([jong, key]);

          const newValue =
            valueBeforeCursor.slice(0, -1) +
            newFirstChar +
            newSecondChar +
            valueAfterCursor;

          nativeInputValueSetter.call(activeInput, newValue);
          const newCursorPos = cursorPos + 1;
          activeInput.setSelectionRange(newCursorPos, newCursorPos);
          activeInput.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }

        // 일반 조합
        const combined = Hangul.assemble([...disassembled, key]);

        if (combined && combined !== lastChar + key) {
          // 조합 성공
          const newValue =
            valueBeforeCursor.slice(0, -1) + combined + valueAfterCursor;

          nativeInputValueSetter.call(activeInput, newValue);
          activeInput.setSelectionRange(cursorPos, cursorPos);
          activeInput.dispatchEvent(new Event('input', { bubbles: true }));
          return;
        }
      }

      // 조합 실패 또는 새 글자 시작
      const newValue = valueBeforeCursor + key + valueAfterCursor;
      nativeInputValueSetter.call(activeInput, newValue);
      const newCursorPos = cursorPos + 1;
      activeInput.setSelectionRange(newCursorPos, newCursorPos);
      activeInput.dispatchEvent(new Event('input', { bubbles: true }));
    },
    [activeInput, nativeInputValueSetter]
  );

  // 영어 키 입력 처리
  const handleEnglishKeyPress = useCallback(
    (key: string) => {
      if (!activeInput || !nativeInputValueSetter) return;

      const currentValue = activeInput.value;
      const cursorPos = activeInput.selectionStart || 0;
      const cursorEnd = activeInput.selectionEnd || 0;

      const valueBeforeCursor = currentValue.substring(0, cursorPos);
      const valueAfterCursor = currentValue.substring(cursorEnd);

      const newValue = valueBeforeCursor + key + valueAfterCursor;
      nativeInputValueSetter.call(activeInput, newValue);
      const newCursorPos = cursorPos + key.length;
      activeInput.setSelectionRange(newCursorPos, newCursorPos);
      activeInput.dispatchEvent(new Event('input', { bubbles: true }));
    },
    [activeInput, nativeInputValueSetter]
  );

  // 키 입력 처리
  const handleKeyPress = useCallback(
    (key: string) => {
      if (currentLanguage === 'ko' && /[ㄱ-ㅎㅏ-ㅣ]/.test(key)) {
        handleKoreanKeyPress(key);
      } else {
        handleEnglishKeyPress(key);
      }

      // Shift는 한 번만 적용
      if (isShifted) {
        setIsShifted(false);
      }
    },
    [currentLanguage, isShifted, handleKoreanKeyPress, handleEnglishKeyPress]
  );

  // Backspace 처리
  const handleBackspace = useCallback(() => {
    if (!activeInput || !nativeInputValueSetter) return;

    const currentValue = activeInput.value;
    const cursorPos = activeInput.selectionStart || 0;
    const cursorEnd = activeInput.selectionEnd || 0;

    if (cursorPos === 0 && cursorEnd === 0) return;

    // 선택 영역이 있으면 삭제
    if (cursorPos !== cursorEnd) {
      const newValue =
        currentValue.substring(0, cursorPos) + currentValue.substring(cursorEnd);
      nativeInputValueSetter.call(activeInput, newValue);
      activeInput.setSelectionRange(cursorPos, cursorPos);
      activeInput.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    // 한글 분해 처리
    const lastChar = currentValue.charAt(cursorPos - 1);
    const isKorean = /[가-힣]/.test(lastChar);

    if (isKorean && currentLanguage === 'ko') {
      const disassembled = Hangul.disassemble(lastChar);

      if (disassembled.length > 1) {
        // 마지막 자모 제거
        const remaining = disassembled.slice(0, -1);
        const reassembled = Hangul.assemble(remaining);

        const newValue =
          currentValue.substring(0, cursorPos - 1) +
          reassembled +
          currentValue.substring(cursorPos);

        nativeInputValueSetter.call(activeInput, newValue);
        activeInput.setSelectionRange(cursorPos, cursorPos);
        activeInput.dispatchEvent(new Event('input', { bubbles: true }));
        return;
      }
    }

    // 일반 삭제
    const newValue =
      currentValue.substring(0, cursorPos - 1) + currentValue.substring(cursorPos);
    nativeInputValueSetter.call(activeInput, newValue);
    const newCursorPos = cursorPos - 1;
    activeInput.setSelectionRange(newCursorPos, newCursorPos);
    activeInput.dispatchEvent(new Event('input', { bubbles: true }));
  }, [activeInput, currentLanguage, nativeInputValueSetter]);

  // Enter 처리
  const handleEnter = useCallback(() => {
    if (activeInput) {
      activeInput.blur();
    }
  }, [activeInput]);

  // Shift 토글
  const handleShift = useCallback(() => {
    setIsShifted((prev) => !prev);
  }, []);

  // 언어 전환
  const handleLanguageToggle = useCallback(() => {
    setCurrentLanguage((prev) => (prev === 'ko' ? 'en' : 'ko'));
    setIsShifted(false);
  }, []);

  return (
    <>
      {children}
      <VirtualKeyboard
        visible={visible}
        onKeyPress={handleKeyPress}
        onBackspace={handleBackspace}
        onEnter={handleEnter}
        onShift={handleShift}
        onLanguageToggle={handleLanguageToggle}
        isShifted={isShifted}
        currentLanguage={currentLanguage}
      />
    </>
  );
}
